/**
 * Topic absorption — trainer feedback report (Meeting Tools docked panel).
 *
 * Views:
 *   topic-wise → group by topic; student name + message only
 *   student-wise → report rows: Topic | Student | Level | Message | Mark Done
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import type { SessionTopicPlan } from "./topicProgress";
import { GRASP_LABELS, type GraspLevel, type GraspVote } from "./liveQuizTypes";

type ReportView = "topic" | "student";

type TopicAbsorptionPanelProps = {
  /** When false, render nothing (used while Meeting Tool is closed). */
  open?: boolean;
  sessionId?: string;
  topics: SessionTopicPlan[];
  currentTopicName?: string;
  graspVotes?: GraspVote[];
  doubtsCount?: number;
  feedbackCount?: number;
  /** Optional — Meeting Tools uses its own Back control. */
  onClose?: () => void;
};

function voteKey(v: GraspVote): string {
  return `${v.student_id}:${v.topic_id}:${v.submitted_at || 0}`;
}

function doneStorageKey(sessionId: string): string {
  return `codelive.graspDoneByTrainer:${sessionId}`;
}

function readDoneKeys(sessionId: string): Set<string> {
  if (!sessionId || typeof window === "undefined") return new Set();
  try {
    const raw = window.sessionStorage.getItem(doneStorageKey(sessionId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

function writeDoneKeys(sessionId: string, keys: Set<string>): void {
  if (!sessionId || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      doneStorageKey(sessionId),
      JSON.stringify(Array.from(keys)),
    );
  } catch {
    /* ignore quota */
  }
}

function levelColor(level: GraspLevel): string {
  if (level === "understood") return "#6ee7b7";
  if (level === "partial") return "#fbbf24";
  if (level === "not_answered") return "#94a3b8";
  return "#f87171";
}

function displayName(name: string): string {
  return String(name || "").trim() || "Student";
}

/** Doubts = Partially Understood + Did Not Understand (excludes Understood). */
function isAbsorptionVote(v: GraspVote): boolean {
  return v.level === "partial" || v.level === "not_understood";
}

function AbsorptionCountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      title={`${count} absorption (excludes Understood)`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 8,
        borderRadius: 999,
        padding: "2px 8px",
        fontSize: 11,
        fontWeight: 800,
        fontVariantNumeric: "tabular-nums",
        background: "rgba(245,158,11,0.18)",
        border: "1px solid rgba(245,158,11,0.4)",
        color: "#fbbf24",
        verticalAlign: "middle",
      }}
    >
      {count}
    </span>
  );
}

export function TopicAbsorptionPanel({
  open = true,
  sessionId = "",
  topics,
  currentTopicName,
  graspVotes = [],
}: TopicAbsorptionPanelProps) {
  const [view, setView] = useState<ReportView>("topic");
  const [doneKeys, setDoneKeys] = useState<Set<string>>(() => new Set());
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    // ---------------------------------------------------------------
    // step 1: Restore trainer "mark done" keys for this session
    // ---------------------------------------------------------------
    if (!open || !sessionId) return;
    setDoneKeys(readDoneKeys(sessionId));
  }, [open, sessionId]);

  const markDone = useCallback(
    (vote: GraspVote) => {
      // ---------------------------------------------------------------
      // step 1: Mark feedback row handled (session-local)
      // ---------------------------------------------------------------
      const key = voteKey(vote);
      setDoneKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        writeDoneKeys(sessionId, next);
        return next;
      });
    },
    [sessionId],
  );

  const sortedVotes = useMemo(() => {
    return [...graspVotes].sort(
      (a, b) => (b.submitted_at || 0) - (a.submitted_at || 0),
    );
  }, [graspVotes]);

  const visibleVotes = useMemo(() => {
    if (showDone) return sortedVotes;
    return sortedVotes.filter((v) => !doneKeys.has(voteKey(v)));
  }, [sortedVotes, doneKeys, showDone]);

  const topicGroups = useMemo(() => {
    // ---------------------------------------------------------------
    // step 1: Group open feedback by topic for topic-wise view
    // ---------------------------------------------------------------
    const map = new Map<string, { title: string; votes: GraspVote[] }>();
    for (const v of visibleVotes) {
      const id = v.topic_id || v.topic_title || "unknown";
      const title = v.topic_title || "Topic";
      const entry = map.get(id) || { title, votes: [] };
      entry.votes.push(v);
      map.set(id, entry);
    }
    return Array.from(map.entries()).map(([id, g]) => ({
      id,
      title: g.title,
      votes: g.votes,
      absorptionCount: g.votes.filter(isAbsorptionVote).length,
    }));
  }, [visibleVotes]);

  const absorptionByTopicId = useMemo(() => {
    const map = new Map<string, number>();
    for (const v of sortedVotes) {
      if (!isAbsorptionVote(v)) continue;
      const id = v.topic_id || "";
      if (!id) continue;
      map.set(id, (map.get(id) || 0) + 1);
    }
    // Also match by title when topic_id is missing
    for (const t of topics) {
      if (map.has(t.id)) continue;
      const byTitle = sortedVotes.filter(
        (v) =>
          isAbsorptionVote(v) &&
          (v.topic_title || "").trim().toLowerCase() ===
            t.name.trim().toLowerCase(),
      ).length;
      if (byTitle > 0) map.set(t.id, byTitle);
    }
    return map;
  }, [sortedVotes, topics]);

  const studentGroups = useMemo(() => {
    // ---------------------------------------------------------------
    // step 1: Group by student for student-wise report
    // ---------------------------------------------------------------
    const map = new Map<string, { name: string; votes: GraspVote[] }>();
    for (const v of visibleVotes) {
      const id = v.student_id || v.student_name || "unknown";
      const name = displayName(v.student_name);
      const entry = map.get(id) || { name, votes: [] };
      entry.votes.push(v);
      map.set(id, entry);
    }
    return Array.from(map.entries())
      .map(([id, g]) => ({ id, ...g }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleVotes]);

  const flatItems: Array<{
    id: string;
    name: string;
    durationMin: number;
    completed: boolean;
    isSubtopic?: boolean;
    parentName?: string;
  }> = [];
  for (const t of topics) {
    if (t.subtopics && t.subtopics.length > 0) {
      for (const s of t.subtopics) {
        flatItems.push({ ...s, isSubtopic: true, parentName: t.name });
      }
    } else {
      flatItems.push({
        id: t.id,
        name: t.name,
        durationMin: t.durationMin,
        completed: t.completed,
      });
    }
  }

  if (!open) return null;

  const completed = flatItems.filter((t) => t.completed).length;
  // Feedback = all grasp responses; Doubts = partial + did not understand only
  const totalFeedback = sortedVotes.length;
  const totalDoubts = sortedVotes.filter(isAbsorptionVote).length;
  const openDoubts = sortedVotes.filter(
    (v) => isAbsorptionVote(v) && !doneKeys.has(voteKey(v)),
  ).length;

  return (
    <div
      className="topic-absorption-panel"
      style={{
        margin: -4,
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "contain",
        paddingRight: 4,
      }}
    >
      <style>{`
        .topic-absorption-panel {
          scrollbar-width: thin;
          scrollbar-color: rgba(148,163,184,0.45) transparent;
        }
        .topic-absorption-panel::-webkit-scrollbar { width: 8px; }
        .topic-absorption-panel::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,0.45);
          border-radius: 999px;
        }
        .topic-absorption-panel::-webkit-scrollbar-track { background: transparent; }
      `}</style>
      <p style={{ margin: "0 0 12px", fontSize: 11, color: "#64748b" }}>
        Live doubts & feedback · {openDoubts} open
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <SummaryCard label="Doubts" value={totalDoubts} tone="amber" />
        <SummaryCard label="Feedback" value={totalFeedback} tone="blue" />
      </div>

      <p
        style={{
          margin: "0 0 8px",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "#64748b",
        }}
      >
        Topics · {completed}/{flatItems.length} complete
      </p>
      {currentTopicName ? (
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#60a5fa" }}>
          Ongoing: {currentTopicName}
        </p>
      ) : null}

      <ul style={{ listStyle: "none", margin: "0 0 16px", padding: 0 }}>
        {topics.map((topic) => (
          <li key={topic.id}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                padding: "8px 0",
                borderBottom: topic.subtopics?.length ? "none" : "1px solid rgba(255,255,255,0.06)",
                fontSize: 13,
              }}
            >
              <span style={{ color: "#e2e8f0", fontWeight: topic.subtopics?.length ? 600 : 400 }}>
                {topic.name}
                <AbsorptionCountBadge
                  count={absorptionByTopicId.get(topic.id) || 0}
                />
              </span>
              <span
                style={{
                  color: topic.completed ? "#6ee7b7" : "#94a3b8",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {topic.completed ? "Done" : `${topic.durationMin}m`}
              </span>
            </div>
            {topic.subtopics && topic.subtopics.length > 0 && (
              <ul style={{ listStyle: "none", margin: 0, padding: "0 0 0 16px" }}>
                {topic.subtopics.map((sub) => (
                  <li
                    key={sub.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "5px 0",
                      borderBottom: "1px solid rgba(255,255,255,0.04)",
                      fontSize: 12,
                    }}
                  >
                    <span style={{ color: "#cbd5e1" }}>{sub.name}</span>
                    <span
                      style={{
                        color: sub.completed ? "#6ee7b7" : "#94a3b8",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sub.completed ? "Done" : `${sub.durationMin}m`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
          marginBottom: 14,
        }}
      >
        <ViewToggle view={view} onChange={setView} />
        <label
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            color: "#94a3b8",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={showDone}
            onChange={(e) => setShowDone(e.target.checked)}
          />
          Show done
        </label>
      </div>

      {visibleVotes.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>
          {sortedVotes.length === 0
            ? "Student grasp votes will appear here once post-topic feedback is collected."
            : "All feedback marked as done. Toggle “Show done” to review."}
        </p>
      ) : view === "topic" ? (
        <TopicWiseView
          groups={topicGroups}
          doneKeys={doneKeys}
          onMarkDone={markDone}
        />
      ) : (
        <StudentWiseView
          groups={studentGroups}
          doneKeys={doneKeys}
          onMarkDone={markDone}
        />
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "amber" | "blue";
}) {
  const amber = tone === "amber";
  return (
    <div
      style={{
        borderRadius: 12,
        background: amber
          ? "rgba(245,158,11,0.12)"
          : "rgba(59,130,246,0.12)",
        border: amber
          ? "1px solid rgba(245,158,11,0.3)"
          : "1px solid rgba(59,130,246,0.3)",
        padding: 14,
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: amber ? "#fbbf24" : "#93c5fd",
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: "6px 0 0",
          fontSize: 28,
          fontWeight: 700,
          color: amber ? "#fde68a" : "#bfdbfe",
        }}
      >
        {value}
      </p>
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: ReportView;
  onChange: (v: ReportView) => void;
}) {
  const btn = (id: ReportView, label: string) => {
    const active = view === id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => onChange(id)}
        style={{
          border: active
            ? "1px solid rgba(96,165,250,0.5)"
            : "1px solid rgba(255,255,255,0.1)",
          borderRadius: 8,
          background: active ? "rgba(59,130,246,0.2)" : "transparent",
          color: active ? "#bfdbfe" : "#94a3b8",
          fontSize: 11,
          fontWeight: 700,
          padding: "6px 12px",
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div style={{ display: "inline-flex", gap: 6 }}>
      {btn("topic", "Topic-wise")}
      {btn("student", "Student-wise")}
    </div>
  );
}

function MarkDoneButton({
  done,
  onClick,
}: {
  done: boolean;
  onClick: () => void;
}) {
  if (done) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          fontSize: 11,
          fontWeight: 600,
          color: "#6ee7b7",
          whiteSpace: "nowrap",
        }}
      >
        <Check size={12} /> Done
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: "1px solid rgba(110,231,183,0.35)",
        borderRadius: 8,
        background: "rgba(16,185,129,0.12)",
        color: "#6ee7b7",
        fontSize: 11,
        fontWeight: 700,
        padding: "5px 10px",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      Mark done
    </button>
  );
}

/** Topic-wise: only student name + message under each topic */
function TopicWiseView({
  groups,
  doneKeys,
  onMarkDone,
}: {
  groups: {
    id: string;
    title: string;
    votes: GraspVote[];
    absorptionCount: number;
  }[];
  doneKeys: Set<string>;
  onMarkDone: (v: GraspVote) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {groups.map((group) => (
        <section key={group.id}>
          <p
            style={{
              margin: "0 0 8px",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0,
            }}
          >
            {group.title}
            <AbsorptionCountBadge count={group.absorptionCount} />
          </p>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {group.votes.map((vote) => {
              const key = voteKey(vote);
              const done = doneKeys.has(key);
              return (
                <li
                  key={key}
                  style={{
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.1)",
                    background: done
                      ? "rgba(255,255,255,0.02)"
                      : "rgba(255,255,255,0.04)",
                    padding: "10px 12px",
                    opacity: done ? 0.65 : 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#fff",
                        }}
                      >
                        {displayName(vote.student_name)}
                      </p>
                      <p
                        style={{
                          margin: "6px 0 0",
                          fontSize: 13,
                          color: "#e2e8f0",
                          lineHeight: 1.45,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {vote.message?.trim() ||
                          GRASP_LABELS[vote.level] ||
                          "—"}
                      </p>
                    </div>
                    {isAbsorptionVote(vote) ? (
                      <MarkDoneButton
                        done={done}
                        onClick={() => onMarkDone(vote)}
                      />
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * Student-wise report:
 * Topic Name | Student Name | Grasp Level | Message | Mark Done
 */
function StudentWiseView({
  groups,
  doneKeys,
  onMarkDone,
}: {
  groups: { id: string; name: string; votes: GraspVote[] }[];
  doneKeys: Set<string>;
  onMarkDone: (v: GraspVote) => void;
}) {
  const rows = groups.flatMap((g) =>
    g.votes.map((vote) => ({ vote, studentName: g.name })),
  );

  return (
    <div
      className="topic-absorption-scroll"
      style={{
        overflowX: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <style>{`
        .topic-absorption-scroll::-webkit-scrollbar { display: none; }
      `}</style>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 12,
        }}
      >
        <thead>
          <tr>
            {(
              [
                "Topic Name",
                "Student Name",
                "Grasp Level",
                "Message",
                "Action",
              ] as const
            ).map((h) => (
              <th
                key={h}
                style={{
                  textAlign: "left",
                  padding: "8px 6px",
                  borderBottom: "1px solid rgba(255,255,255,0.12)",
                  color: "#94a3b8",
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ vote, studentName }) => {
            const key = voteKey(vote);
            const done = doneKeys.has(key);
            const level = vote.level as GraspLevel;
            return (
              <tr key={key} style={{ opacity: done ? 0.6 : 1 }}>
                <td
                  style={{
                    padding: "10px 6px",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    color: "#e2e8f0",
                    verticalAlign: "top",
                    maxWidth: 120,
                  }}
                >
                  {vote.topic_title || "Topic"}
                </td>
                <td
                  style={{
                    padding: "10px 6px",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    color: "#fff",
                    fontWeight: 600,
                    verticalAlign: "top",
                    whiteSpace: "nowrap",
                  }}
                >
                  {studentName}
                </td>
                <td
                  style={{
                    padding: "10px 6px",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    color: levelColor(level),
                    fontWeight: 700,
                    verticalAlign: "top",
                    whiteSpace: "nowrap",
                  }}
                >
                  {GRASP_LABELS[level] || level}
                </td>
                <td
                  style={{
                    padding: "10px 6px",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    color: "#cbd5e1",
                    verticalAlign: "top",
                    lineHeight: 1.4,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {vote.message?.trim() || "—"}
                </td>
                <td
                  style={{
                    padding: "10px 6px",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    verticalAlign: "top",
                  }}
                >
                  {isAbsorptionVote(vote) ? (
                    <MarkDoneButton
                      done={done}
                      onClick={() => onMarkDone(vote)}
                    />
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
